import { useState, useEffect } from "react";
import "@/App.css";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import TradingDashboard from "./pages/TradingDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import { Toaster } from "./components/ui/sonner";

// ✅ domínio real do backend (produção)
const CANONICAL_ORIGIN = "https://www.cofy.app";

function isIp(hostname) {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(String(hostname || ""));
}

function isLocalHost(hostname) {
  const h = String(hostname || "").toLowerCase();
  return h === "localhost" || isIp(h);
}

// ✅ Se estiver rodando local (IP/localhost), força o backend do domínio
const ORIGIN = isLocalHost(window.location.hostname)
  ? CANONICAL_ORIGIN
  : window.location.origin;

// ✅ Se o LoginPage salvou a base usada, aproveita (melhor)
const API_FROM_LOGIN = localStorage.getItem("api_base_used");

// 🔗 Backend do TRADER (base final)
const BACKEND_URL = API_FROM_LOGIN || `${ORIGIN}/api/trader`;

// Constante usada em todo o frontend para montar a URL base da API
export const API = BACKEND_URL;

// Log só pra conferência no console
console.log("[App] BACKEND_URL em uso:", API);

// Axios interceptor para token (compatível: token antigo + access_token novo)
axios.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("access_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const rawToken =
      localStorage.getItem("token") || localStorage.getItem("access_token");
    const rawUser = localStorage.getItem("user");

    if (!rawToken || !rawUser) {
      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setLoading(false);
      return;
    }

    if (rawUser === "undefined" || rawUser === "null" || rawUser === "") {
      console.warn("[App] Valor inválido em localStorage(user):", rawUser);
      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setLoading(false);
      return;
    }

    try {
      const parsed = JSON.parse(rawUser);
      if (parsed && typeof parsed === "object") {
        setUser(parsed);
      } else {
        console.warn("[App] user parseado não é objeto:", parsed);
        localStorage.removeItem("token");
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
      }
    } catch (err) {
      console.error("[App] Erro ao fazer JSON.parse do user:", rawUser, err);
      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData, token) => {
    if (!userData || typeof userData !== "object") {
      console.error("[App] handleLogin recebeu userData inválido:", userData);
      return;
    }
    if (!token || typeof token !== "string") {
      console.error("[App] handleLogin recebeu token inválido:", token);
      return;
    }

    // ✅ mantém compatibilidade com o que você já tinha…
    localStorage.setItem("token", token);
    // ✅ …e também salva no padrão novo (pra não dar conflito com outras telas)
    localStorage.setItem("access_token", token);

    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <HashRouter>
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user && user.role === "user" ? (
                <TradingDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && user.role === "admin" ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/"
            element={
              <Navigate
                to={
                  user
                    ? user.role === "admin"
                      ? "/admin"
                      : "/dashboard"
                    : "/login"
                }
              />
            }
          />
        </Routes>
      </HashRouter>
    </div>
  );
}

export default App;